import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Dropdown } from 'react-bootstrap';
import { useAuth } from '../lib/auth.jsx';

// Top-level navigation items, then collapsible groups for everything else.
const PRIMARY = [
  { to: '/',           icon: 'tabler:dashboard',     label: 'Übersicht' },
  // Einnahmen vereint Rechnungen + Abos auf einer Seite mit zwei Tabs.
  { to: '/income',     icon: 'tabler:cash',          label: 'Einnahmen',  perm: ['invoices', 'read'] },
  { to: '/expenses',   icon: 'tabler:wallet',        label: 'Ausgaben',   perm: ['expenses', 'read'] },
  { to: '/customers',  icon: 'tabler:building',      label: 'Kunden',     perm: ['customers', 'read'] },
  { to: '/orders',     icon: 'tabler:shopping-cart', label: 'Bestellungen', perm: ['orders', 'read'] },
  { to: '/licenses',   icon: 'tabler:certificate',   label: 'Lizenzen',   perm: ['licenses', 'read'] }
];

const SECONDARY = [
  { to: '/accounting',     icon: 'tabler:book',           label: 'Buchhaltung',    perm: ['accounting', 'read'] },
  { to: '/features',       icon: 'tabler:adjustments',    label: 'Feature-Katalog',perm: ['features', 'read'] },
  { to: '/billing-config', icon: 'tabler:settings-dollar',label: 'Billing-Konfig', perm: ['discount_codes', 'read'] },
  { to: '/audit',          icon: 'tabler:history',        label: 'Audit-Log',      perm: ['audit', 'read'] }
];

const ADMIN = [
  { to: '/users',     icon: 'tabler:users',         label: 'Benutzer',     perm: ['users', 'read'] },
  { to: '/roles',     icon: 'tabler:shield-lock',   label: 'Rollen',       perm: ['roles', 'read'] },
  { to: '/api-keys',  icon: 'tabler:key',           label: 'API-Keys',     perm: ['api_keys', 'read'] },
  { to: '/settings',  icon: 'tabler:settings-2',    label: 'Einstellungen',perm: ['settings', 'read'] }
];

function NavItem({ item, can }) {
  if (item.perm && !can(item.perm[0], item.perm[1])) return null;
  return (
    <NavLink to={item.to} end={item.to === '/'} className="nav-link">
      <Icon icon={item.icon} className="nav-icon" />
      <span>{item.label}</span>
    </NavLink>
  );
}

function NavSection({ label, items, can }) {
  const visible = items.filter(it => !it.perm || can(it.perm[0], it.perm[1]));
  const storageKey = `nav-section:${label}`;
  // Initial state from localStorage so collapse/expand survives F5.
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  const toggle = () => {
    setOpen(o => {
      const next = !o;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };
  if (visible.length === 0) return null;
  return (
    <>
      <button type="button" className="nav-section nav-section-toggle" onClick={toggle}>
        <span>{label}</span>
        <Icon icon={open ? 'tabler:chevron-down' : 'tabler:chevron-right'} />
      </button>
      {open && visible.map(it => <NavItem key={it.to} item={it} can={can} />)}
    </>
  );
}

export default function AdminLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Auf jedem Routenwechsel die Session sanity-checken — sofortiger Logout
  // wenn der Server uns nicht mehr kennt.
  useEffect(() => { auth.validateSession?.(); }, [location.pathname, auth.validateSession]);
  const handleLogout = async () => { await auth.logout(); navigate('/login'); };

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <div className="brand">
          <span className="brand-mark">●</span>&nbsp;UAV-BOS&nbsp;<small style={{ opacity: 0.6 }}>Kontor</small>
        </div>
        <nav className="flex-grow-1">
          {PRIMARY.map(it => <NavItem key={it.to} item={it} can={auth.can} />)}
          <NavSection label="Mehr"        items={SECONDARY} can={auth.can} />
          <NavSection label="Verwaltung"  items={ADMIN}     can={auth.can} />
        </nav>
        <div className="sidebar-footer">v0.1.0</div>
      </aside>

      <header className="app-header">
        <div></div>
        <Dropdown align="end">
          <Dropdown.Toggle as="button" id="user-menu" className="user-menu-toggle">
            <Icon icon="tabler:user-circle" style={{ fontSize: 22 }} className="me-2" />
            <span className="d-none d-md-inline">{auth.user?.display_name || auth.user?.email}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Header>
              <div className="fw-semibold">{auth.user?.display_name}</div>
              <div className="text-muted small">{auth.user?.email}</div>
              <div className="mt-1 d-flex flex-wrap gap-1">
                {(auth.user?.roles || []).map(r => (
                  <span key={r.id} className="badge badge-soft-primary">{r.name}</span>
                ))}
              </div>
            </Dropdown.Header>
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => navigate('/profile')}>
              <Icon icon="tabler:settings" className="me-2" />Profil & MFA
            </Dropdown.Item>
            <Dropdown.Item onClick={handleLogout}>
              <Icon icon="tabler:logout" className="me-2" />Abmelden
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </header>

      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}
